<template>
  <teleport to="body">
    <div class="editor-overlay" @keydown.esc="onCancel">
      <!-- Header -->
      <div class="editor-header">
        <div class="editor-header-left">
          <div class="editor-icon" :style="{ background: iconBg }">
            <v-icon size="18" :color="iconColor">{{ icon }}</v-icon>
          </div>
          <div class="editor-titles">
            <div class="editor-title">{{ title }}</div>
            <div v-if="filePath" class="editor-path">{{ filePath }}</div>
          </div>
        </div>
        <div class="editor-header-right">
          <span v-if="dirty" class="editor-dirty-pill">
            <span class="editor-dirty-dot"></span>
            Ungespeicherte Änderungen
          </span>
          <button class="editor-btn editor-btn-ghost" @click="onCancel" :disabled="saving">
            <v-icon size="15">mdi-close</v-icon>
            <span>Schließen</span>
          </button>
          <button class="editor-btn editor-btn-primary" :disabled="!dirty || saving" @click="onSave">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-content-save-outline</v-icon>
            <span>Speichern</span>
          </button>
        </div>
      </div>

      <!-- Statusbar (oben) -->
      <div class="editor-statusbar">
        <span class="editor-stat">
          <v-icon size="13" color="#82AAFF">mdi-code-tags</v-icon>
          {{ language || 'plaintext' }}
        </span>
        <span class="editor-stat">{{ lineCount }} Zeilen · {{ charCount }} Zeichen</span>
        <span v-if="dirty" class="editor-stat editor-stat-dirty">geändert</span>
        <span v-else class="editor-stat editor-stat-clean">gespeichert</span>
        <span class="editor-shortcut" title="Speichern: Cmd/Ctrl+S · Schließen: Esc">⌘S speichern</span>
      </div>

      <!-- Monaco Container -->
      <div ref="editorContainer" class="editor-container"></div>
    </div>
  </teleport>
</template>

<script>
import * as monaco from 'monaco-editor';
import { markRaw } from 'vue';
import { registerShopifyLiquid } from './monaco-shopify-liquid';

export default {
  name: 'CodeEditor',
  props: {
    content: { type: String, default: '' },
    language: { type: String, default: 'html' },
    title: { type: String, default: 'Code-Editor' },
    filePath: { type: String, default: '' },
    icon: { type: String, default: 'mdi-code-braces' },
    iconColor: { type: String, default: '#2E7D32' },
    iconBg: { type: String, default: 'rgba(46,125,50,0.1)' },
    saving: { type: Boolean, default: false },
  },
  emits: ['save', 'close'],

  // WICHTIG: editor-Instanz und der ständig aktualisierte Inhalt liegen NICHT
  // in data() — sonst proxiet Vue 3 den Monaco-Editor (riesiger interner
  // Graph mit zirkulären Refs) und JEDER Tastendruck blockiert den Hauptthread.
  // Wir weisen `this.editor` in mountEditor() einfach als reguläres Instanz-
  // Property zu (nicht reaktiv) und sichern es zusätzlich mit markRaw().
  data() {
    return {
      lineCount: 0,
      charCount: 0,
      dirty: false,
    };
  },

  watch: {
    content(val) {
      if (!this.editor) return;
      // Wenn der Parent neuen Inhalt liefert (extern oder nach Save), holen wir
      // den ggf. ab. ABER auch wenn Editor & neuer Wert identisch sind (typisch
      // direkt nach Save), müssen wir dirty=false setzen — sonst „klebt" das
      // Pill „Ungespeicherte Änderungen" am Editor.
      if (val !== this.editor.getValue()) {
        this.editor.setValue(val || '');
      }
      this.dirty = false;
      this.refreshStats();
    },
    language(val) {
      if (this.editor) {
        const model = this.editor.getModel();
        if (model) monaco.editor.setModelLanguage(model, val);
      }
    },
  },

  mounted() {
    this.$nextTick(() => this.mountEditor());
  },

  beforeUnmount() {
    this.disposeEditor();
  },

  methods: {
    mountEditor() {
      if (this.editor || !this.$refs.editorContainer) return;

      registerShopifyLiquid();

      // markRaw verhindert, dass Vue 3 die Editor-Instanz in einen reaktiven
      // Proxy verpackt — entscheidend für Tipp-Performance.
      this.editor = markRaw(monaco.editor.create(this.$refs.editorContainer, {
        value: this.content || '',
        language: this.language,
        theme: 'floow-shopify',
        fontSize: 13,
        fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, 'Courier New', monospace",
        lineNumbers: 'on',
        roundedSelection: false,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        minimap: { enabled: true },
        wordWrap: 'on',
        tabSize: 2,
        insertSpaces: true,
        renderWhitespace: 'selection',
        // Bewusst aus: bei großen HTML/Liquid-Dateien mit vielen <>-Klammern
        // hat der Bracket-Pair-Scanner zu spürbaren Hängern beim Klick geführt.
        bracketPairColorization: { enabled: false },
        // Smooth-Animationen halten in einigen Builds RAF-Loops am Leben → off.
        smoothScrolling: false,
        cursorSmoothCaretAnimation: 'off',
        // Hover/Parameter-Hints abschalten — wir brauchen keine Sprach-Services
        // (Validation/IntelliSense), nur Syntax-Highlighting. Das spart CPU
        // und verhindert, dass HTML-Worker bei jedem Klick anlaufen.
        hover: { enabled: false },
        parameterHints: { enabled: false },
        quickSuggestions: false,
        suggestOnTriggerCharacters: false,
        occurrencesHighlight: 'off',
        codeLens: false,
        folding: true,
        foldingStrategy: 'indentation',
        padding: { top: 10, bottom: 10 },
        suggest: { showWords: false },
      }));

      this.dirty = false;
      this.refreshStats();
      this.applySchemaDecorations();

      // Change-Handler ohne Throttle — markRaw + nicht-reaktiver Editor sorgen
      // dafür, dass der Hauptthread nicht mehr durch Vue-Reactivity blockiert
      // wird, also können wir Stats + dirty synchron pro Keystroke aktualisieren.
      // Das hält den Save-Button zuverlässig korrekt enabled.
      this._contentChangeDisposable = this.editor.onDidChangeModelContent(() => {
        if (!this.editor) return;
        const v = this.editor.getValue();
        this.dirty = v !== (this.content || '');
        this.charCount = v.length;
        this.lineCount = v ? v.split('\n').length : 0;
        this.scheduleSchemaDecorations();
      });

      this.editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => this.onSave());
      this.editor.addCommand(monaco.KeyCode.Escape, () => this.onCancel());

      setTimeout(() => this.editor && this.editor.focus(), 50);
    },

    // Schema-Highlighting mit gedrosseltem Refresh (max. alle 250 ms).
    // Decorations sind ein leichtgewichtiges Visual-Overlay über dem getokenten
    // Text — sie greifen nicht in die Tokenization ein und können daher keinen
    // Hänger verursachen. Das gibt uns das colorful JSON-Highlighting im
    // schema-Block zurück, ohne dass wir einen eigenen Monarch-Tokenizer
    // brauchen.
    scheduleSchemaDecorations() {
      if (this._schemaDecoTimer) return;
      this._schemaDecoTimer = setTimeout(() => {
        this._schemaDecoTimer = null;
        this.applySchemaDecorations();
      }, 250);
    },

    applySchemaDecorations() {
      if (!this.editor) return;
      // Nur für HTML/Liquid-Dateien sinnvoll
      if (this.language !== 'html') {
        if (this._schemaDecorations && this._schemaDecorations.length) {
          this._schemaDecorations = this.editor.deltaDecorations(this._schemaDecorations, []);
        }
        return;
      }
      const model = this.editor.getModel();
      if (!model) return;

      const text = model.getValue();
      const decorations = [];

      // Pattern erfasst {% schema %} ... {% endschema %} (mit optionalem `-` für Whitespace-Stripping)
      const blockRe = /(\{%-?\s*schema\s*-?%\})([\s\S]*?)(\{%-?\s*endschema\s*-?%\})/g;
      let block;
      while ((block = blockRe.exec(text)) !== null) {
        const openTag = block[1];
        const body = block[2];
        const closeTag = block[3];
        const blockStart = block.index;
        const bodyStart = blockStart + openTag.length;
        const closeStart = bodyStart + body.length;

        // Schema-Delimiter selbst hervorheben
        decorations.push({
          range: this._rangeFromOffsets(model, blockStart, blockStart + openTag.length),
          options: { inlineClassName: 'flw-schema-delim' },
        });
        decorations.push({
          range: this._rangeFromOffsets(model, closeStart, closeStart + closeTag.length),
          options: { inlineClassName: 'flw-schema-delim' },
        });

        // JSON-Tokens innerhalb des Bodies
        // 1) Strings (Keys & Values)
        const stringRe = /"(?:[^"\\]|\\.)*"/g;
        let s;
        while ((s = stringRe.exec(body)) !== null) {
          const start = bodyStart + s.index;
          const end = start + s[0].length;
          // Key, wenn direkt danach (ggf. nach Whitespace) ein `:` folgt
          const after = body.slice(s.index + s[0].length);
          const isKey = /^\s*:/.test(after);
          decorations.push({
            range: this._rangeFromOffsets(model, start, end),
            options: { inlineClassName: isKey ? 'flw-schema-key' : 'flw-schema-string' },
          });
        }
        // 2) Numbers
        const numRe = /-?\b\d+\.?\d*(?:[eE][+-]?\d+)?\b/g;
        let n;
        while ((n = numRe.exec(body)) !== null) {
          const start = bodyStart + n.index;
          const end = start + n[0].length;
          decorations.push({
            range: this._rangeFromOffsets(model, start, end),
            options: { inlineClassName: 'flw-schema-num' },
          });
        }
        // 3) Booleans + null
        const kwRe = /\b(?:true|false|null)\b/g;
        let k;
        while ((k = kwRe.exec(body)) !== null) {
          const start = bodyStart + k.index;
          const end = start + k[0].length;
          decorations.push({
            range: this._rangeFromOffsets(model, start, end),
            options: { inlineClassName: 'flw-schema-kw' },
          });
        }
      }

      this._schemaDecorations = this.editor.deltaDecorations(
        this._schemaDecorations || [],
        decorations
      );
    },

    _rangeFromOffsets(model, startOffset, endOffset) {
      const s = model.getPositionAt(startOffset);
      const e = model.getPositionAt(endOffset);
      return new monaco.Range(s.lineNumber, s.column, e.lineNumber, e.column);
    },

    disposeEditor() {
      if (this._schemaDecoTimer) {
        clearTimeout(this._schemaDecoTimer);
        this._schemaDecoTimer = null;
      }
      if (this._contentChangeDisposable) {
        try { this._contentChangeDisposable.dispose(); } catch (_) { /* ignore */ }
        this._contentChangeDisposable = null;
      }
      if (this.editor) {
        const model = this.editor.getModel();
        try { this.editor.dispose(); } catch (_) { /* ignore */ }
        if (model) {
          try { model.dispose(); } catch (_) { /* ignore */ }
        }
        this.editor = null;
      }
      const c = this.$refs.editorContainer;
      if (c) {
        while (c.firstChild) c.removeChild(c.firstChild);
      }
    },

    refreshStats() {
      const v = this.editor ? this.editor.getValue() : (this.content || '');
      this.charCount = v.length;
      this.lineCount = v ? v.split('\n').length : 0;
    },

    onSave() {
      if (!this.dirty || this.saving) return;
      const v = this.editor ? this.editor.getValue() : (this.content || '');
      this.$emit('save', v);
    },

    onCancel() {
      if (this.saving) return;
      if (this.dirty) {
        const ok = window.confirm('Du hast ungespeicherte Änderungen. Wirklich schließen?');
        if (!ok) return;
      }
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Material Palenight Editor-Chrome */
.editor-overlay {
  position: fixed; inset: 0; z-index: 3000;
  background: #292D3E;
  display: flex; flex-direction: column;
  animation: editor-fade-in 0.18s ease;
}
@keyframes editor-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.editor-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 18px;
  background: #1F2233;
  border-bottom: 1px solid #191B26;
  flex-shrink: 0;
}
.editor-header-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.editor-header-right { display: flex; align-items: center; gap: 8px; }

.editor-icon {
  width: 36px; height: 36px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.editor-titles { min-width: 0; }
.editor-title {
  font-size: 14px; font-weight: 700; color: #EEFFFF;
  letter-spacing: -0.1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.editor-path {
  font-size: 11px; color: #676E95;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  margin-top: 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.editor-dirty-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px;
  background: rgba(255,203,107,0.15); color: #FFCB6B;
  border-radius: 999px; font-size: 11px; font-weight: 600;
  margin-right: 4px;
}
.editor-dirty-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #FFCB6B;
  animation: dirty-pulse 1.4s ease-in-out infinite;
}
@keyframes dirty-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
}

.editor-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 8px;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer; border: none; font-family: inherit;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.editor-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.editor-btn-ghost {
  background: transparent; color: #A6ACCD;
  border: 1px solid #3B3F51;
}
.editor-btn-ghost:hover:not(:disabled) {
  background: #353A4F; color: #EEFFFF; border-color: #4E5579;
}
.editor-btn-primary {
  background: #82AAFF; color: #1F2233;
}
.editor-btn-primary:hover:not(:disabled) { background: #A6C0FF; }

.editor-statusbar {
  display: flex; align-items: center; gap: 16px;
  padding: 6px 18px;
  background: #1F2233;
  border-bottom: 1px solid #191B26;
  font-size: 11px; color: #676E95;
  flex-shrink: 0;
}
.editor-stat { display: inline-flex; align-items: center; gap: 4px; }
.editor-stat-dirty { color: #FFCB6B; font-weight: 600; }
.editor-stat-clean { color: #C3E88D; font-weight: 600; }
.editor-shortcut { margin-left: auto; font-family: 'SFMono-Regular', Menlo, monospace; color: #A6ACCD; }

.editor-container {
  flex: 1 1 auto;
  min-height: 0;
  background: #292D3E;
}
</style>

<!--
  Globale (un-scoped) Styles für die Schema-Decorations.
  Monaco rendert die inlineClassName-Decorations innerhalb seiner eigenen
  DOM-Hierarchie — Vues `scoped`-Suffixe greifen dort nicht. Daher hier ohne `scoped`.
  Material Palenight Palette.
-->
<style>
.flw-schema-delim   { color: #C792EA !important; font-weight: 600; }
.flw-schema-key     { color: #F07178 !important; }
.flw-schema-string  { color: #C3E88D !important; }
.flw-schema-num     { color: #F78C6C !important; }
.flw-schema-kw      { color: #FF5370 !important; font-style: italic; }
</style>
